/* Web Audio SFX manager: минимальная задержка и стабильность в Safari.
   Используйте для коротких эффектов (клик, победа, проигрыш). */

export type SfxKey = string;

export class WebAudioSfxManager {
	private audioContext: AudioContext | null = null;
	private gainNodes: Map<SfxKey, GainNode> = new Map();
	private buffers: Map<SfxKey, AudioBuffer> = new Map();
	private userInteracted = false;
	private lastPlayTime: Map<SfxKey, number> = new Map();
	private PLAY_COOLDOWN = 30; // 30ms между воспроизведениями одного звука


	private ensureContext(): AudioContext {
		if (this.audioContext) return this.audioContext;
		
		try {
			// Используем оптимальные настройки для всех устройств
			const options = {
				latencyHint: 'interactive'
			};
			
			this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)(options as any);
			return this.audioContext;
		} catch (error) {
			console.error('❌ Ошибка создания AudioContext:', error);
			throw error;
		}
	}

	public markUserInteracted() {
		this.userInteracted = true;
		
		if (this.audioContext && this.audioContext.state === 'suspended') {
			this.audioContext.resume().catch((error) => {
				console.error('❌ Ошибка возобновления AudioContext:', error);
			});
		}
	}

	public isReady(): boolean {
		return !!this.audioContext && this.userInteracted;
	}

	public async preload(key: SfxKey, url: string, volume = 0.5): Promise<void> {
		try {
			const ctx = this.ensureContext();
			
			// Если уже загружено — обновим громкость и выйдем
			if (this.buffers.has(key)) {
				const node = this.gainNodes.get(key);
				if (node) node.gain.value = volume;
				return;
			}
			
			// Загружаем и декодируем
			const resp = await fetch(url, { cache: 'force-cache' });
			if (!resp.ok) {
				throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
			}
			
			const data = await resp.arrayBuffer();
			const buffer = await ctx.decodeAudioData(data);
			
			this.buffers.set(key, buffer);
			const gain = ctx.createGain();
			gain.gain.value = volume;
			gain.connect(ctx.destination);
			this.gainNodes.set(key, gain);
		} catch (error) {
			console.error(`❌ Ошибка предзагрузки "${key}":`, error);
			throw error;
		}
	}

	public play(key: SfxKey, options?: { volume?: number }): void {
		// Защита от множественных вызовов
		const now = Date.now();
		const lastPlay = this.lastPlayTime.get(key) || 0;
		if (now - lastPlay < this.PLAY_COOLDOWN) {
			return; // Тихий игнор быстрых вызовов
		}
		this.lastPlayTime.set(key, now);
		
		const ctx = this.ensureContext();
		if (!this.userInteracted) return;
		
		const buffer = this.buffers.get(key);
		if (!buffer) return;
		
		if (ctx.state === 'suspended') {
			ctx.resume().catch(() => {});
		}
		
		const gain = this.gainNodes.get(key);
		if (!gain) return;
		
		if (options?.volume != null) gain.gain.value = options.volume;
		
		// Создаем источник на каждый проигрыш (по правилам WebAudio)
		const src = ctx.createBufferSource();
		src.buffer = buffer;
		src.connect(gain);
		
		// Воспроизведение с fade-in для всех устройств
		try {
			const now = ctx.currentTime;
			const initial = Math.max(0.0001, gain.gain.value * 0.5);
			gain.gain.setValueAtTime(0.0001, now);
			gain.gain.linearRampToValueAtTime(initial, now + 0.01);
			src.start();
			// Быстрый возврат к целевой громкости
			gain.gain.linearRampToValueAtTime(options?.volume ?? initial * 2, now + 0.06);
		} catch (error) {
			console.error(`❌ Ошибка воспроизведения "${key}":`, error);
		}
	}

	public stopAll(): void {
		// GainNodes остаются, источники одноразовые — глушим выход
		this.gainNodes.forEach(g => (g.gain.value = 0));
	}

	public async cleanup(): Promise<void> {
		this.buffers.clear();
		this.gainNodes.clear();
		if (this.audioContext) {
			try { await this.audioContext.close(); } catch {}
			this.audioContext = null;
		}
		this.userInteracted = false;
	}

	public reinitialize(): void {
		// Сбрасываем состояние для переинициализации
		this.userInteracted = false;
		this.lastPlayTime.clear();
		
		// Если AudioContext существует, попробуем его возобновить
		if (this.audioContext && this.audioContext.state === 'suspended') {
			this.audioContext.resume().catch(() => {});
		}
	}
}

export const webAudioSfx = new WebAudioSfxManager();
